import fuzzysort from 'fuzzysort';
import {Type} from 'io-ts';
import {parseIncompletePhoneNumber, parsePhoneNumberFromString} from 'libphonenumber-js/max';
import {isUndefined, uniqBy} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {addQueryToPath} from '../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {
  useCancelableEffect,
  useDebounce,
} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {UnreachableError} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {CancelError, CancelToken} from '../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {ApiType} from '../../../libs/shared/core/src/models/api/commonApiTypes';
import {CompanyModel} from '../../../libs/shared/core/src/models/api/company/companyModel';
import {InboxModel} from '../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {fetchApiAsync} from '../../../libs/shared/core/src/services/http/http';
import {getFlatActiveTeammates} from '../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {getHasSearchEnhanceContactsAutosuggest} from '../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {
  trackSearchClientSideFilteringDiscrepancy,
  trackSearchSuggestionsBulkLatency,
} from '../../../src/components/inboxes/search/searchTrackingHelpers';
import {useAppSelector} from '../../../src/helpers/appReduxHelpers';
import {
  buildTagSearchUrl,
  getTagAliasDuplicates,
  SearchTagStatusesEnum,
} from '../../../src/helpers/models/tagHelpers';
import {AccountModel} from '../../../src/models/api/accountModel';
import {
  ApiRecipientContactMatches,
  RecipientContactMatchModel,
} from '../../../src/models/api/recipientMatchModel';
import {ApiSearchAccountsSuggestions} from '../../../src/models/api/searchAccountsV2Model';
import {ApiSearchTagsSuggestions, SearchTagsSuggestion} from '../../../src/models/api/searchTagModel';
import {
  ApiSmartMixMatches,
  ApiSmartMixMatchesBulk,
  maybeGetSmartMixMatchResourceId,
  RefilterableSmartMixMatchesModel,
  SmartMixClassnamesEnum,
  SmartMixMatchModel,
  SmartMixTagMatchModel,
} from '../../../src/models/api/smartMixMatchModel';
import {TagBehaviorsEnum} from '../../../src/models/api/tagModel';
import {buildRecipientContactMatchSearchUrl} from '../../../src/services/recipientMatches';
import {buildSearchAccountsV2Url} from '../../../src/store/data/accounts/accountsActions';
import {getCompany} from '../../../src/store/data/company/companySelectors';
import {
  getUserOwnedInboxes,
  getUserTeamParticipatingInboxes,
} from '../../../src/store/data/inboxes/inboxesSelectors';
import {
  getCellsSearchQueryHash,
  getCellsSearchRecentQueries,
} from '../../../src/store/panels/cells/search/cellsPanelSearchSelectors';
import {createSelector} from '../../helpers/reselectHelpers';
import {refilterNonMatching, refilterWithLimit} from './clientSideFiltering/filtering';
import {
  containsNonSupportedCharacter,
  simpleCharacterNormalization,
  splitOnNonAlphaNum,
  splitOnWhitespace,
} from './clientSideFiltering/textAnalysis';
import {ModifiersEnum} from './searchModifiersHelpers';
import {
  isInvalidDotToken,
  isSearchModifierToken,
  isWildcardToken,
  SearchModifierToken,
  SearchToken,
} from './searchQueryHelpers';
import {
  fuzzySortBaseOptions,
  getPropertiesSuggestions,
  InboxesSearchDropdownRecentQuery,
  InboxesSearchDropdownSuggestion,
  InboxesSearchDropdownSuggestionBase,
  maybePromoteTeammateSuggestions,
  shouldInsertWhitespace,
  SuggestionSelectHandlerOptions,
} from './searchSuggestionsHelpers';

/**
 * The search dropdown is composed of multiple sections:
 *   - recent queries (aka recent searches), i.e. the queries that the user made previously,
 *   - modifier helpers (aka advanced filters), only displayed if the current token is a wildcard token,
 *   - suggestions, see below,
 *   - conversations suggestions (aka top results).
 *
 * Suggestions are based on the active token, i.e. the token from the query currently under the cursor.
 *
 * We do have 2 types of suggestions, that are never displayed at the same time:
 *   - modifier suggestions, i.e. suggestions related to a modifier.
 *   - smart mix, i.e. a mix of different resources.
 *
 * Modifier suggestions are available for these modifiers:
 *   - inboxes
 *   - author, assignee, participant, mention, commenter (for teammates)
 *   - tags
 *   - recipient, to, from, cc, bcc (for contacts)
 *   - account
 *   - type
 *   - has
 *   - is
 *   - in
 *   - before, after, during
 */

/*
 * Constants.
 */

const MAX_DISPLAYED_RECENT_QUERIES = 3;
const MAX_DISPLAYED_SUGGESTIONS = 5;
// As we might remove suggestions that are already recent queries, fetch enough suggestions.
const MAX_FETCH_SUGGESTIONS = MAX_DISPLAYED_RECENT_QUERIES + MAX_DISPLAYED_SUGGESTIONS;
const MAX_SMART_MIX_SUGGESTIONS = 4;

// Be careful messing with this debounce value, as the impact on the number of
// queries hitting the endpoint can be unpredictable.
// For instance, in Q2 2023, this was reduced from 250ms to 200ms,
// which lead to a 30%-45% increase in the number of queries hitting the search
// suggestions endpoint.
const SEARCH_SUGGESTIONS_DEBOUNCE_WAIT_MS = 200;

// For partial phone numbers, libphonenumber-js is unable to produce "national" versions that include the trunk prefix
// for countries that have one. We need to add plausible trunk prefixes.
const COMMON_TRUNK_PREFIXES = ['0', '1', '8', '06'];

const inboxSuggestionItemOptions: SuggestionItemOptions<InboxModel> = {
  valueGetter: (inbox) => inbox.alias,
  makeAdditionalProps: (inbox) => ({label: inbox.name}),
};

const teammateSuggestionItemOptions: SuggestionItemOptions<TeammateModel> = {
  valueGetter: (teammate) => teammate.alias,
  makeAdditionalProps: (teammate) => ({teammate}),
};

const tagSuggestionItemOptions: SuggestionItemOptions<SearchTagsSuggestion> = {
  keyGetter: ({tag}) => `${tag.namespace}-${tag.alias}`,
  valueGetter: ({tag}) => tag.alias,
  makeAdditionalProps: ({tag, tagsPath}, allItems) => ({
    tag,
    tagsPath,
    shouldShowDeduplication: getTagAliasDuplicates(allItems.map((item) => item.tagsPath)).has(tag.alias),
  }),
};

const recipientContactSuggestionItemOptions: SuggestionItemOptions<RecipientContactMatchModel> = {
  valueGetter: (recipient) => recipient.handle,
  makeAdditionalProps: (recipient) => ({contactMatch: recipient}),
};

const accountSuggestionItemOptions: SuggestionItemOptions<AccountModel> = {
  valueGetter: (account) => String(account.alias),
  makeAdditionalProps: (account) => ({account}),
};

/*
 * Types.
 */

type SuggestionItemOptions<T> = {
  keyGetter?: (item: T) => InboxesSearchDropdownSuggestion['key'];
  valueGetter: (item: T) => InboxesSearchDropdownSuggestion['value'];
  makeAdditionalProps: (
    item: T,
    allItems: ReadonlyArray<T>,
  ) => Omit<InboxesSearchDropdownSuggestion, keyof InboxesSearchDropdownSuggestionBase>;
};

interface SearchSuggestions {
  recentQueries: ReadonlyArray<InboxesSearchDropdownRecentQuery>;
  suggestions: ReadonlyArray<InboxesSearchDropdownSuggestion>;
}

/*
 * Hook.
 */

export function useSearchSuggestions(
  query: string | undefined,
  activeTokens: ReadonlyArray<SearchToken>,
  namespace?: string,
): SearchSuggestions {
  const sanitizedQuery = query ? query.trim().replace(/\s+/g, ' ') : '';

  const queryHash = useAppSelector(getCellsSearchQueryHash);

  // Recent queries.
  const allRecentQueries = useAppSelector(getCellsSearchRecentQueries);

  const filteredRecentQueries = useMemo(
    () =>
      (allRecentQueries || [])
        .filter((recentQuery) => sanitizedQuery.length === 0 || recentQuery.startsWith(sanitizedQuery))
        .slice(0, MAX_DISPLAYED_RECENT_QUERIES),
    [sanitizedQuery, allRecentQueries],
  );

  // Suggestions.
  const company = useAppSelector(getCompany);
  const inboxes = useAppSelector(getUserInboxes);
  const teammates = useAppSelector(getEnhancedTeammates);

  const [suggestions, setSuggestions] = useState<ReadonlyArray<InboxesSearchDropdownSuggestion>>([]);

  // Client-side filtering.
  const shouldEnhanceContactsAutosuggest = useAppSelector(getHasSearchEnhanceContactsAutosuggest);

  const activeTokensValue = activeTokens.map(({value}) => value).join(' ');

  // A defined value will trigger the query.
  const bulkSuggestionQuery = computeBulkSuggestionsQuery(
    shouldEnhanceContactsAutosuggest,
    activeTokens,
    activeTokensValue,
  );

  const [refilterableSmartMixMatches, setRefilterableSmartMixMatches] = useState<
    RefilterableSmartMixMatchesModel | undefined
  >(undefined);

  // To enable client-side filtering, we need all the following to be true:
  const isActivelyRefiltering = useMemo(
    () =>
      // - We MUST have fetched refilterable suggestions.
      isDefined(refilterableSmartMixMatches) &&
      // - The backend does NOT have more suggestions matching the bulk query.
      !refilterableSmartMixMatches.hasMoreContactSuggestions &&
      !refilterableSmartMixMatches.hasMoreSuggestions &&
      // - The backend was not able to normalize some token characters.
      !refilterableSmartMixMatches.hasUnsupportedCharacter &&
      // - The query does NOT contain non-supported characters
      !containsNonSupportedCharacter(simpleCharacterNormalization(activeTokensValue)) &&
      // - The query is NOT a modifier for which we have specific handling.
      !hasModifierSuggestions(activeTokens) &&
      // - The query is not an HTTP URL.
      !isValidHttpUrl(activeTokensValue),
    [activeTokens, activeTokensValue, refilterableSmartMixMatches],
  );

  const fetchSuggestionsAsync = async (
    searchTokens: ReadonlyArray<SearchToken>,
    cancelToken: CancelToken,
  ) => {
    try {
      if (searchTokens.length === 1) {
        const [searchToken] = searchTokens;

        if (
          isUndefined(searchToken.value) ||
          isWildcardToken(searchToken, query) ||
          isInvalidDotToken(searchToken)
        ) {
          return setSuggestions([]);
        }

        const encodedSearchTokenValue = encodeURIComponent(searchToken.value);

        // If the token is a modifier one, we might want to display modifier suggestions.
        if (isSearchModifierToken(searchToken)) {
          switch (searchToken.modifier) {
            case ModifiersEnum.TAG: {
              const path = buildTagSearchUrl(company, encodedSearchTokenValue, {
                offset: 0,
                limit: MAX_FETCH_SUGGESTIONS,
                behaviors: [TagBehaviorsEnum.CAN_SUGGEST],
                status: SearchTagStatusesEnum.ALL,
                source: 'search-modifier-suggestions-tags',
              });

              const items = await fetchAsyncItems(
                searchToken,
                ApiSearchTagsSuggestions,
                path,
                cancelToken,
                tagSuggestionItemOptions,
              );

              return setSuggestions(items);
            }

            case ModifiersEnum.RECIPIENT:
            case ModifiersEnum.TO:
            case ModifiersEnum.FROM:
            case ModifiersEnum.CC:
            case ModifiersEnum.BCC: {
              const path = buildRecipientContactMatchSearchUrl(company, encodedSearchTokenValue, {
                preferredNamespace: namespace,
                limit: MAX_FETCH_SUGGESTIONS,
              });

              const items = await fetchAsyncItems(
                searchToken,
                ApiRecipientContactMatches,
                path,
                cancelToken,
                recipientContactSuggestionItemOptions,
                (matches) => uniqBy(matches, (item) => item.handle),
              );

              return setSuggestions(items);
            }

            case ModifiersEnum.ACCOUNT: {
              const path = buildSearchAccountsV2Url(company.url, {
                query: encodedSearchTokenValue,
                limit: MAX_FETCH_SUGGESTIONS,
                suggestions: true,
              });

              const items = await fetchAsyncItems(
                searchToken,
                ApiSearchAccountsSuggestions,
                path,
                cancelToken,
                accountSuggestionItemOptions,
              );

              return setSuggestions(items);
            }

            case ModifiersEnum.CUSTOM_FIELD:
              // Do not suggest anything for now.
              return setSuggestions([]);

            default:
              break;
          }
        }
      }

      // Otherwise, try to display smart mix suggestions.
      const items = await fetchSmartMixItems(searchTokens, cancelToken, {
        company,
        queryHash,
        namespace,
        teammates,
        isEnhancingContactsAutosuggest: shouldEnhanceContactsAutosuggest,
      });

      return setSuggestions(items);
    } catch (e) {
      if (!(e instanceof CancelError)) {
        throw e;
      }

      return null;
    }
  };

  const fetchSuggestionsAsyncDebounced = useDebounce(
    fetchSuggestionsAsync,
    SEARCH_SUGGESTIONS_DEBOUNCE_WAIT_MS,
  );

  const updateSuggestionsAsync = useCallback(
    async (searchTokens: ReadonlyArray<SearchToken>, cancelToken: CancelToken) => {
      // The requests are canceled via the cancel token, but we still need to
      // cancel the debounce timer.
      fetchSuggestionsAsyncDebounced.cancel();

      if (searchTokens.length === 1) {
        const [searchToken] = searchTokens;

        if (isUndefined(searchToken.value) || isWildcardToken(searchToken, query)) {
          return setSuggestions([]);
        }

        // If the token is a modifier one, we might want to display modifier suggestions.
        if (isSearchModifierToken(searchToken)) {
          switch (searchToken.modifier) {
            case ModifiersEnum.INBOX:
              return setSuggestions(
                searchStoreItems(searchToken, inboxes, ['name', 'alias'], inboxSuggestionItemOptions),
              );

            case ModifiersEnum.AUTHOR:
            case ModifiersEnum.ASSIGNEE:
            case ModifiersEnum.PARTICIPANT:
            case ModifiersEnum.MENTION:
            case ModifiersEnum.COMMENTER:
              return setSuggestions(
                searchStoreItems(
                  searchToken,
                  teammates,
                  ['displayName', 'alias'],
                  teammateSuggestionItemOptions,
                ),
              );

            case ModifiersEnum.TYPE:
            case ModifiersEnum.CHANNEL_TYPE:
            case ModifiersEnum.HAS:
            case ModifiersEnum.IS:
            case ModifiersEnum.IN:
            case ModifiersEnum.BEFORE:
            case ModifiersEnum.AFTER:
            case ModifiersEnum.DURING:
              return setSuggestions(searchStaticItems(searchToken));

            default:
              break;
          }
        }
      }

      return fetchSuggestionsAsyncDebounced(searchTokens, cancelToken);
    },
    [fetchSuggestionsAsyncDebounced, inboxes, query, teammates],
  );

  // When the bulk suggestions query changes:
  // - remove previously fetched refilterable suggestions, if any
  // - fetch a new set of refilterable suggestions
  useCancelableEffect(
    (cancelToken) => {
      if (bulkSuggestionQuery) {
        const requestStartTimeMs = performance.now();
        fetchBulkSmartMixMatches(bulkSuggestionQuery, cancelToken, {
          company,
          queryHash,
          namespace,
        })
          .then((newRefilterableSmartMixMatches) => {
            setRefilterableSmartMixMatches(newRefilterableSmartMixMatches);

            // Run advanced checks and tracking 10% of the time.
            if (Math.random() < 0.1) {
              // We expect every result to match the query on the client, track discrepancies.
              reportDiscrepancies(newRefilterableSmartMixMatches, bulkSuggestionQuery, queryHash, namespace);

              const elapsedMs = performance.now() - requestStartTimeMs;
              trackSearchSuggestionsBulkLatency({
                elapsedMs,
                contactSuggestionsCount: newRefilterableSmartMixMatches.contactSuggestions.length,
                suggestionsCount: newRefilterableSmartMixMatches.suggestions.length,
              });
            }
          })
          .catch((err) => {
            // Ignore CancelError.
            if (!(err instanceof CancelError)) {
              throw err;
            }
          });
      }

      return () => {
        setRefilterableSmartMixMatches(undefined);
      };
    },
    [bulkSuggestionQuery, company, queryHash, namespace],
  );

  // When not actively refiltering and the query changes, fetch a new set of suggestions.
  useCancelableEffect(
    (cancelToken) => {
      if (!isActivelyRefiltering && activeTokens.length) {
        updateSuggestionsAsync(activeTokens, cancelToken);
      }
    },
    [isActivelyRefiltering, activeTokens, updateSuggestionsAsync],
  );

  const computeRefilteredSuggestions = useCallback(
    (refilteringQuery: string, refilterableSuggestions: RefilterableSmartMixMatchesModel) => {
      const refilteredSuggestions = refilterSmartMixMatches(refilterableSuggestions, refilteringQuery);
      return postProcessSmartMixMatches(refilteredSuggestions, refilteringQuery, teammates, namespace, {
        isClientSideFiltering: true,
        isEnhancingContactsAutosuggest: false,
      });
    },
    [teammates, namespace],
  );

  // When actively refiltering and the query changes, refilter bulk suggestions on the client.
  useEffect(() => {
    // `isActivelyRefiltering` implies `refilterableSmartMixMatches`, but TS doesn't
    // look through the `useMemo`.
    if (isActivelyRefiltering && refilterableSmartMixMatches) {
      const refilteredSuggestions = computeRefilteredSuggestions(
        activeTokensValue,
        refilterableSmartMixMatches,
      );
      setSuggestions(refilteredSuggestions);
    }
  }, [isActivelyRefiltering, activeTokensValue, refilterableSmartMixMatches, computeRefilteredSuggestions]);

  return {
    recentQueries: filteredRecentQueries.map((recentQuery) => ({
      value: recentQuery,
      fuzzyQuery: sanitizedQuery,
      onSelectOptions: {
        insertWhiteSpace: false,
        replaceWholeQuery: true,
        isRecentQuery: true,
      },
    })),
    // Filter out suggestions that are duplicates of recent queries.
    // TODO(ldemarne 2023-10-12): Move this closer to suggestions computation.
    suggestions: suggestions
      .filter(({value}) => !filteredRecentQueries.includes(value))
      .slice(0, MAX_DISPLAYED_SUGGESTIONS),
  };
}

/*
 * Helpers.
 */

const getUserInboxes = createSelector(
  [getUserOwnedInboxes, getUserTeamParticipatingInboxes],
  (userOwnedInboxes, userTeamParticipatingInboxes) => userOwnedInboxes.concat(userTeamParticipatingInboxes),
);

const getEnhancedTeammates = createSelector([getFlatActiveTeammates], (teammates) =>
  teammates.map((t) => ({
    ...t,
    displayName: buildTeammateDisplayName(t),
  })),
);

function searchStoreItems<T>(
  activeToken: SearchModifierToken,
  storeItems: ReadonlyArray<T>,
  searchKeys: ReadonlyArray<keyof T>,
  options: SuggestionItemOptions<T>,
): ReadonlyArray<InboxesSearchDropdownSuggestion> {
  const {keyGetter, valueGetter, makeAdditionalProps} = options;

  const onSelectOptions = {
    insertWhiteSpace: true,
    isModifierSuggestion: true,
    modifierSuggestionType: activeToken.modifier,
  };

  // Return default items if value is empty.
  if (isUndefined(activeToken.value) || activeToken.value.length === 0) {
    const slicedItems = storeItems.slice(0, MAX_FETCH_SUGGESTIONS);
    return slicedItems.map((item): InboxesSearchDropdownSuggestion => {
      const value = `${activeToken.modifier}:${valueGetter(item)}`;

      const additionalProps = makeAdditionalProps(item, slicedItems) ?? {};

      return {
        key: keyGetter?.(item),
        modifier: activeToken.modifier,
        value,
        onSelectOptions,
        fuzzyQuery: activeToken.value ?? '',
        ...additionalProps,
      };
    });
  }

  // Otherwise, search in store.
  const fuzzyResults = fuzzysort
    .go(activeToken.value, [...storeItems], {
      ...fuzzySortBaseOptions,
      keys: searchKeys.map((key) => String(key)),
    })
    .slice(0, MAX_FETCH_SUGGESTIONS);

  const allItems = fuzzyResults.map((r) => r.obj);

  return fuzzyResults.map((item): InboxesSearchDropdownSuggestion => {
    const value = `${activeToken.modifier}:${valueGetter(item.obj)}`;

    const additionalProps = makeAdditionalProps(item.obj, allItems) ?? {};

    return {
      key: keyGetter?.(item.obj),
      modifier: activeToken.modifier,
      value,
      onSelectOptions,
      fuzzyQuery: activeToken.value ?? '',
      ...additionalProps,
    };
  });
}

async function fetchAsyncItems<T>(
  activeToken: SearchModifierToken,
  apiType: ApiType<Type<ReadonlyArray<T>, ReadonlyArray<any>, unknown>>,
  path: string,
  cancelToken: CancelToken,
  options: SuggestionItemOptions<T>,
  transformMatches?: (matches: ReadonlyArray<T>) => ReadonlyArray<T>,
): Promise<ReadonlyArray<InboxesSearchDropdownSuggestion>> {
  const {keyGetter, valueGetter, makeAdditionalProps} = options;

  const onSelectOptions = {
    insertWhiteSpace: true,
    isModifierSuggestion: true,
    modifierSuggestionType: activeToken.modifier,
  };

  const matches = await fetchApiAsync(apiType, path, {cancelToken});
  const transformedMatches = transformMatches?.(matches) ?? matches;

  return transformedMatches.map((match): InboxesSearchDropdownSuggestion => {
    const value = `${activeToken.modifier}:${valueGetter(match)}`;

    const additionalProps = makeAdditionalProps(match, transformedMatches) ?? {};

    return {
      key: keyGetter?.(match),
      modifier: activeToken.modifier,
      value,
      onSelectOptions,
      fuzzyQuery: activeToken.value ?? '',
      ...additionalProps,
    };
  });
}

function searchStaticItems(activeToken: SearchModifierToken): ReadonlyArray<InboxesSearchDropdownSuggestion> {
  const suggestions = getPropertiesSuggestions(activeToken);

  const onSelectOptionsBase = {
    isModifierSuggestion: true,
    modifierSuggestionType: activeToken.modifier,
  };

  if (isUndefined(activeToken.value) || activeToken.value.length === 0) {
    return suggestions.map((suggestion) => ({
      ...suggestion,
      onSelectOptions: {
        ...onSelectOptionsBase,
        insertWhiteSpace: !('valuePlaceholder' in suggestion),
      },
    }));
  }

  return fuzzysort
    .go(activeToken.value, [...suggestions], {
      ...fuzzySortBaseOptions,
      key: 'value',
    })
    .map((item) => ({
      ...item.obj,
      onSelectOptions: {
        ...onSelectOptionsBase,
        insertWhiteSpace: !('valuePlaceholder' in item.obj),
      },
    }));
}

async function fetchBulkSmartMixMatches(
  activeTokensValue: string,
  cancelToken: CancelToken,
  options: {
    company: CompanyModel;
    queryHash: string | undefined;
    namespace: string | undefined;
  },
) {
  const {company, queryHash, namespace} = options;

  const encodedQuery = encodeURIComponent(activeTokensValue);

  const path = addQueryToPath(`${company.url}/search_suggestions_bulk/${encodedQuery}`, {
    namespace,
    query_hash: queryHash,
  });

  return fetchApiAsync(ApiSmartMixMatchesBulk, path, {cancelToken});
}

async function fetchSmartMixItems(
  activeTokens: ReadonlyArray<SearchToken>,
  cancelToken: CancelToken,
  options: {
    company: CompanyModel;
    queryHash: string | undefined;
    namespace: string | undefined;
    teammates: ReadonlyArray<TeammateModel>;
    isEnhancingContactsAutosuggest: boolean;
  },
): Promise<ReadonlyArray<InboxesSearchDropdownSuggestion>> {
  if (activeTokens.length === 1) {
    const [activeToken] = activeTokens;

    if (isInvalidDotToken(activeToken) || isUndefined(activeToken.value)) {
      return [];
    }
  }

  const {company, queryHash, namespace, teammates} = options;

  const activeTokensValue = activeTokens.map(({value}) => value).join(' ');
  const encodedQuery = encodeURIComponent(activeTokensValue);

  const path = addQueryToPath(`${company.url}/search_suggestions/${encodedQuery}`, {
    namespace,
    query_hash: queryHash,
  });

  const smartMixMatches = await fetchApiAsync(ApiSmartMixMatches, path, {cancelToken});
  return postProcessSmartMixMatches(smartMixMatches, activeTokensValue, teammates, namespace, {
    isClientSideFiltering: false,
    isEnhancingContactsAutosuggest: options.isEnhancingContactsAutosuggest,
  });
}

function postProcessSmartMixMatches(
  matches: ReadonlyArray<SmartMixMatchModel>,
  activeTokensValue: string,
  teammates: ReadonlyArray<TeammateModel>,
  namespace: string | undefined,
  {
    isClientSideFiltering,
    isEnhancingContactsAutosuggest,
  }: {isClientSideFiltering: boolean; isEnhancingContactsAutosuggest: boolean},
) {
  const slicedMatches = matches.slice(0, MAX_SMART_MIX_SUGGESTIONS);

  const tagAliasDuplicates = getTagAliasDuplicates(
    slicedMatches
      .filter((i): i is SmartMixTagMatchModel => i.class === SmartMixClassnamesEnum.TAG)
      .map((i) => i.tagsPath),
  );

  const commonTrackingOptions: Partial<SuggestionSelectHandlerOptions> = {
    isSmartMixItem: true,
    query: activeTokensValue,
    smartMixNamespace: namespace,
    isClientSideFiltering,
    isEnhancingContactsAutosuggest,
  };

  return slicedMatches.map((match, index) =>
    smartMixItemToSuggestion(
      match,
      teammates,
      tagAliasDuplicates,
      activeTokensValue,
      index,
      commonTrackingOptions,
    ),
  );
}

function smartMixItemToSuggestion(
  item: SmartMixMatchModel,
  teammates: ReadonlyArray<TeammateModel>,
  tagAliasDuplicates: Set<string>,
  fuzzyQuery: string,
  index: number,
  commonTrackingOptions: Partial<SuggestionSelectHandlerOptions>,
): InboxesSearchDropdownSuggestion {
  const {primaryLabel, secondaryLabel, score} = item;

  const onSelectOptions: SuggestionSelectHandlerOptions = {
    ...commonTrackingOptions,
    insertWhiteSpace: shouldInsertWhitespace(item.value),
    smartMixType: item.class,
    smartMixValue: item.value,
    smartMixIndex: index,
    smartMixScore: score,
    smartMixResourceId: maybeGetSmartMixMatchResourceId(item),
  };

  const suggestionBase = {
    key: `${item.class}-${item.value}`,
    modifier: item.modifier.name,
    value: item.value,
    onSelectOptions,
    score,
    fuzzyQuery,
    isSmartMixSuggestion: true,
  };

  switch (item.class) {
    case SmartMixClassnamesEnum.ACCOUNT:
      return {
        ...suggestionBase,
        account: item.account,
      };

    case SmartMixClassnamesEnum.DYNAMIC_OBJECT:
      return {
        ...suggestionBase,
        label: primaryLabel,
        dynamicObjectType: item.type,
      };

    case SmartMixClassnamesEnum.CONTACT:
      return {
        ...suggestionBase,
        contactMatch: item.recipient,
        fuzzyQuery: computeContactFuzzyQuery(
          commonTrackingOptions.isEnhancingContactsAutosuggest ?? false,
          fuzzyQuery,
        ),
      };

    case SmartMixClassnamesEnum.INBOX:
      return {
        ...suggestionBase,
        label: primaryLabel,
      };

    case SmartMixClassnamesEnum.MODIFIER:
      return {
        ...suggestionBase,
        valuePlaceholder: secondaryLabel,
      };

    case SmartMixClassnamesEnum.PROPERTY:
      return {
        ...suggestionBase,
      };

    case SmartMixClassnamesEnum.TAG: {
      // We need to reverse the tags path because it's reversed again in `renderTagsPathName`.
      const reversedTagsPath = [...item.tagsPath].reverse();

      return {
        ...suggestionBase,
        key: `${item.value}-${item.tag.namespace}`,
        tag: item.tag,
        tagsPath: reversedTagsPath,
        shouldShowDeduplication: tagAliasDuplicates.has(item.tag.alias),
      };
    }

    case SmartMixClassnamesEnum.TEAMMATE: {
      const teammate = teammates.find(({id}) => id === item.teammateId);

      if (!teammate) {
        return {
          ...suggestionBase,
          label: primaryLabel,
        };
      }

      return {
        ...suggestionBase,
        teammate,
      };
    }

    default:
      throw new UnreachableError(item);
  }
}

function refilterSmartMixMatches(
  refilterableSmartMixMatches: RefilterableSmartMixMatchesModel,
  query: string,
) {
  const contactsQueryTokens = splitOnWhitespace(simpleCharacterNormalization(query));
  const refilteredStaticSuggestions = refilterWithLimit(
    refilterableSmartMixMatches.staticSuggestions,
    contactsQueryTokens,
    1,
  );

  const refilteredContactSuggestions = refilterWithLimit(
    refilterableSmartMixMatches.contactSuggestions,
    contactsQueryTokens,
    4,
  );

  const queryTokens = contactsQueryTokens.flatMap(splitOnNonAlphaNum);
  const refilteredSuggestions = refilterWithLimit(refilterableSmartMixMatches.suggestions, queryTokens, 4);

  const firstContactSuggestions = refilteredContactSuggestions;
  const firstGeneralSuggestions = refilteredSuggestions;

  const {personSuggestions, suggestions} = maybePromoteTeammateSuggestions(
    firstContactSuggestions,
    firstGeneralSuggestions,
  );

  // If we have less than two person suggestions, show as many general suggestions as possible.
  if (personSuggestions.length < 2) {
    return [...refilteredStaticSuggestions, ...personSuggestions, ...suggestions];
  }

  // If we have less than two general suggestions, try to show more person suggestions after general suggestions.
  if (suggestions.length < 2) {
    return [
      ...refilteredStaticSuggestions,
      personSuggestions[0],
      personSuggestions[1],
      ...suggestions,
      ...personSuggestions.slice(2),
    ];
  }

  // If we have at least two person suggestions and two general suggestions, show two of each.
  return [
    ...refilteredStaticSuggestions,
    personSuggestions[0],
    personSuggestions[1],
    suggestions[0],
    suggestions[1],
  ];
}

function hasModifierSuggestions(activeTokens: ReadonlyArray<SearchToken>) {
  // As of 2023-10-17, the only modifiers that are not part of this list are:
  // BODY
  // ID
  // OBJECT
  // SUBJECT
  // CUSTOM_FIELD
  // LINK
  // WITH

  return (
    activeTokens.length === 1 &&
    isSearchModifierToken(activeTokens[0]) &&
    [
      ModifiersEnum.INBOX,
      ModifiersEnum.AUTHOR,
      ModifiersEnum.ASSIGNEE,
      ModifiersEnum.PARTICIPANT,
      ModifiersEnum.MENTION,
      ModifiersEnum.COMMENTER,
      ModifiersEnum.TYPE,
      ModifiersEnum.CHANNEL_TYPE,
      ModifiersEnum.HAS,
      ModifiersEnum.IS,
      ModifiersEnum.IN,
      ModifiersEnum.BEFORE,
      ModifiersEnum.AFTER,
      ModifiersEnum.DURING,
      ModifiersEnum.TAG,
      ModifiersEnum.RECIPIENT,
      ModifiersEnum.TO,
      ModifiersEnum.FROM,
      ModifiersEnum.CC,
      ModifiersEnum.BCC,
      ModifiersEnum.ACCOUNT,
    ].includes(activeTokens[0].modifier)
  );
}

function reportDiscrepancies(
  refilterableSmartMixMatches: RefilterableSmartMixMatchesModel,
  bulkQuery: string,
  queryHash: string | undefined,
  namespace: string | undefined,
) {
  const contactsQueryTokens = splitOnWhitespace(simpleCharacterNormalization(bulkQuery));
  const nonMatchingStaticSuggestions = refilterNonMatching(
    refilterableSmartMixMatches.staticSuggestions,
    contactsQueryTokens,
  ).map((s) => s.value);

  const nonMatchingContactSuggestions = refilterNonMatching(
    refilterableSmartMixMatches.contactSuggestions,
    contactsQueryTokens,
  ).map((s) => s.value);

  const queryTokens = contactsQueryTokens.flatMap(splitOnNonAlphaNum);
  const nonMatchingSuggestions = refilterNonMatching(
    refilterableSmartMixMatches.suggestions,
    queryTokens,
  ).map((s) => s.value);

  if (
    nonMatchingStaticSuggestions.length ||
    nonMatchingContactSuggestions.length ||
    nonMatchingSuggestions.length
  ) {
    trackSearchClientSideFilteringDiscrepancy({
      queryHash,
      query: bulkQuery,
      namespace,
      nonMatchingStaticSuggestions,
      nonMatchingContactSuggestions,
      nonMatchingSuggestions,
    });
  }
}

function isValidHttpUrl(string: string) {
  try {
    const url = new URL(string);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
}

function computeBulkSuggestionsQuery(
  shouldEnhanceContactsAutosuggest: boolean,
  activeTokens: ReadonlyArray<SearchToken>,
  activeTokensValue: string,
) {
  const isPotentialPhoneNumber = activeTokensValue.match(/^\+?[\d(). -]+$/);
  if (shouldEnhanceContactsAutosuggest && isPotentialPhoneNumber) {
    return undefined;
  }

  // The first three characters of the query will be used with the bulk suggestions endpoint.
  if (activeTokensValue.length >= 3 && !hasModifierSuggestions(activeTokens)) {
    return [...activeTokensValue].slice(0, 3).join('');
  }

  return undefined;
}

/**
 * Add extra fuzzy queries for contact suggestions we found via phone number.
 *
 * We want to highlight matches on either the national or international versions of the same number.
 */
function computeContactFuzzyQuery(shouldEnhanceContactsAutosuggest: boolean, fuzzyQuery: string) {
  if (!shouldEnhanceContactsAutosuggest) {
    return fuzzyQuery;
  }

  const strippedQuery = parseIncompletePhoneNumber(fuzzyQuery);
  const parsed = parsePhoneNumberFromString(strippedQuery, 'US');

  if (!parsed) {
    // We didn't find a phone number, so return the original query.
    return fuzzyQuery;
  }

  // The backend is going to find contact suggestions based on the international & national versions (or our best guess
  // to that effect). This means we need to try to compute both versions here so that we can highlight them both in the
  // UI.
  //
  // Unfortunately, libphonenumber-js doesn't provide a way to get the national version with the trunk prefix for short
  // numbers, so we might need to generate versions with plausible trunk prefixes. These just won't match anything if
  // when they're the wrong ones.
  const nationalized = parsed.formatNational().replace(/\D/g, '');
  const nationalisedStartsWithTrunkPrefix = COMMON_TRUNK_PREFIXES.some((prefix) =>
    nationalized.startsWith(prefix),
  );
  const nationalizedWithTrunkPrefixes = nationalisedStartsWithTrunkPrefix
    ? [nationalized]
    : COMMON_TRUNK_PREFIXES.map((prefix) => `${prefix}${nationalized}`);

  return `${parsed.number} ${nationalizedWithTrunkPrefixes.join(' ')}`;
}
